<template>
  <div
      ref="root"
      :class="classes"
  >
    <img
        v-if="imageSrc"
        :src="imageSrc"
        :style="{
              height: imageHeight,
              width: imageWidth,
              display: imageWidth ? 'block' : 'inline-block',
            }"
        :alt="mediaAlt"
    >
    <div
        v-if="enlargeable === '1'"
        class="enlarge-toggle"
        title="Klickem um Bild zu vergrößern"
        @click="enlarge"
    >
      <FontAwesomeIcon :icon="faSearchPlus"/>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getSaveImageUrl } from '@/helpers';
import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
} from 'vue';
import {
  useStore,
} from 'vuex';
import ResponsiveSize from '@/classes/ResponsiveSize';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSearchPlus } from '@fortawesome/pro-light-svg-icons';

library.add(faSearchPlus);

const props = defineProps({
  mediaSizes: {
    type: [String, Object],
    required: false,
  },
  mediaAlt: {
    type: String,
    required: false,
    default: () => null,
  },
  fixedHeight: {
    type: String,
    required: false,
    default: () => '1',
  },
  height: {
    type: String,
    required: false,
  },
  align: {
    type: String,
    required: false,
    default: () => 'center',
  },
  fixedHeightMobile: {
    type: String,
    required: false,
    default: () => '1',
  },
  heightMobile: {
    type: String,
    required: false,
  },
  alignMobile: {
    type: String,
    required: false,
    default: () => 'center',
  },
  enlargeable: {
    type: String,
    required: false,
    default: () => '0',
  },
  isEnlargement: {
    type: Boolean,
    required: false,
    default: () => false,
  },
});

const store = useStore();
const root = ref<InstanceType<typeof HTMLElement>>();

const mediaSources = computed<Record<string, Record<string, unknown>>>(() => {
  let ms = props.mediaSizes as string | Record<string, Record<string, unknown>>;

  if (typeof ms === 'string') {
    try {
      ms = JSON.parse(atob(ms || btoa('{}')));
    } finally {
      if (typeof ms === 'string') {
        ms = {};
      }
    }
  }

  return ms || {};
});

const imageSrc = ref<null | string>(null);
const imageHeight = ref<null | string>(null);
const imageWidth = ref<null | string>(null);
const currentImageSrc = ref<null | Record<string, unknown>>(null);

const setImageSource = () => {
  imageSrc.value = null;

  let sizeKeys = ['full'];

  if (!props.isEnlargement) {
    sizeKeys = ['medium', 'large', 'full'];
  }

  sizeKeys.forEach((key) => {
    if (
      root.value
        && mediaSources.value[key]
        && mediaSources.value[key].width
        && parseInt(`${mediaSources.value[key].width}`, 10) > root.value.offsetWidth
    ) {
      currentImageSrc.value = mediaSources.value[key];
      imageSrc.value = getSaveImageUrl(mediaSources.value[key].url) || '';
    }
  });

  if (imageSrc.value === null) {
    const sourceKeys = Object.keys(mediaSources.value);

    if (sourceKeys.length >= 1) {
      currentImageSrc.value = mediaSources.value.full || mediaSources.value[sourceKeys[0]];
      imageSrc.value = getSaveImageUrl(currentImageSrc.value.url) || '';
    }
  }
};

const setImageHeight = () => {
  if (props.isEnlargement) {
    imageWidth.value = `${currentImageSrc.value?.width}px`;
  } else if (ResponsiveSize.getCurrentBreakpoint() === 'lg') {
    if (parseInt(props.fixedHeight, 10) === 1) {
      imageWidth.value = null;
      imageHeight.value = ResponsiveSize.getCSSValue(props.height as string);
    } else {
      imageHeight.value = null;
      imageWidth.value = '100%';
    }
  } else if (parseInt(props.fixedHeightMobile, 10) === 1) {
    imageWidth.value = null;
    imageHeight.value = `${props.heightMobile}px`;
  } else {
    imageHeight.value = null;
    imageWidth.value = '100%';
  }
};

const setEventHandler = () => {
  window.addEventListener('resize', setImageSource);
  window.addEventListener('load', setImageSource);
  setImageSource();
  if (props.height) {
    window.addEventListener('resize', setImageHeight);
    window.addEventListener('load', setImageHeight);
    setImageHeight();
  }
};

const handleIntersect = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
): void => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      setEventHandler();
      if (root.value) {
        observer.unobserve(root.value);
      }
    }
  });
};

const createObserver = (): void => {
  const observer = new IntersectionObserver(handleIntersect, {
    root: null,
    threshold: 0,
  });

  if (root.value) {
    observer.observe(root.value);
  }
};

onMounted(() => {
  setImageHeight();
  if (window.IntersectionObserver) {
    createObserver();
  } else {
    setEventHandler();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setImageSource);
  window.removeEventListener('load', setImageSource);
  if (props.height) {
    window.removeEventListener('resize', setImageHeight);
    window.removeEventListener('load', setImageHeight);
  }
});

const classes = computed<string[]>(() => {
  const cls = ['io-responsive-image'];

  cls.push(`align-${props.align}`);
  cls.push(`align-mobile-${props.alignMobile}`);

  if (props.enlargeable === '1') {
    cls.push('enlargeable');
  }

  return cls;
});

const enlarge = () => {
  store.commit('enlargement/SET_PROPS', { ...props });
  store.commit('enlargement/SHOW');
};
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.io-responsive-image {
  > img {
    max-width: 100%;
  }

  &.align {
    &-mobile {
      &-center {
        text-align: center;

        > img {
          margin: 0 auto;
        }
      }

      &-start {
        text-align: left;
      }

      &-end {
        text-align: right;
      }
    }

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      &-center {
        text-align: center;

        > img {
          margin: 0 auto;
        }
      }

      &-start {
        text-align: left;
      }

      &-end {
        text-align: right;
      }
    }
  }

  &.enlargeable {
    position: relative;

    .enlarge-toggle {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      opacity: 0;
      transition: opacity .35s ease-in-out;
      cursor: pointer;

      > i, > svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
      }

      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
